import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import api from "../lib/api";

const REQUEST_TYPES = [
  { value: "modification", label: "Modification des informations" },
  { value: "suppression", label: "Suppression de la fiche" },
  { value: "partenariat", label: "Devenir partenaire" },
  { value: "autre", label: "Autre demande" },
];

export default function CompanyCorrection() {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_slug: searchParams.get("slug") || "",
    company_name: searchParams.get("name") || "",
    siret: "",
    requester_first_name: "",
    requester_last_name: "",
    requester_role: "",
    requester_email: "",
    request_type: "modification",
    message: "",
    justification: "",
  });

  useEffect(() => {
    document.title = "Modifier ou retirer une fiche — Déménageur Pro";
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = () =>
    form.company_name.trim() &&
    form.requester_first_name.trim() &&
    form.requester_last_name.trim() &&
    form.requester_email.trim() &&
    form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/company-corrections", form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-6 py-20" data-testid="correction-success">
        <div className="max-w-2xl mx-auto text-center bg-white rounded-3xl border border-[#E5E7EB] p-12 shadow-sm">
          <div className="w-20 h-20 bg-[#E8FBF3] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#2BD391]" />
          </div>
          <div className="eyebrow mb-3">Demande transmise</div>
          <h1 className="font-heading font-extrabold text-4xl mb-4 text-[#2C3E50]">
            Merci de nous avoir contactés
          </h1>
          <p className="text-[#6B7280] mb-2">
            Votre demande concernant la fiche <b className="text-[#2C3E50]">{form.company_name}</b> a bien été enregistrée.
          </p>
          <p className="text-[#6B7280] mb-8">
            Notre équipe l'examinera sous 7 jours ouvrés et vous répondra à
            l'adresse <b className="text-[#2C3E50]">{form.requester_email}</b>.
          </p>
          <Link to="/" className="btn-primary">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up bg-[#F7F9FB] py-12 md:py-20 min-h-screen">
      <div className="container mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10">
            <Link to="/" className="text-xs text-[#6B7280] hover:text-[#2BD391] inline-flex items-center gap-1.5 mb-6">
              <ArrowLeft className="w-3.5 h-3.5" /> Retour
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8FBF3] text-[#1AA66C] text-xs font-semibold mb-4">
              <ShieldCheck className="w-3.5 h-3.5" /> Formulaire dédié aux entreprises
            </div>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl mb-3 text-[#2C3E50] leading-tight">
              Modifier ou retirer<br />une fiche entreprise
            </h1>
            <p className="text-[#6B7280] max-w-2xl">
              Si vous représentez une entreprise référencée dans notre annuaire et souhaitez
              faire modifier ou retirer sa fiche, remplissez ce formulaire. Notre équipe traite
              chaque demande sous 7 jours ouvrés.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm p-6 md:p-10 space-y-6" data-testid="correction-form">
            <fieldset className="space-y-5">
              <legend className="font-heading font-extrabold text-xl mb-1 text-[#2C3E50]">Entreprise concernée</legend>
              <Field label="Nom de l'entreprise *">
                <input className="input-square" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} required data-testid="input-company-name" />
              </Field>
              <Field label="SIRET (si disponible)">
                <input className="input-square" value={form.siret} onChange={(e) => set("siret", e.target.value)} placeholder="14 chiffres" data-testid="input-siret" />
              </Field>
            </fieldset>

            <fieldset className="space-y-5 border-t border-[#F0F2F5] pt-6">
              <legend className="font-heading font-extrabold text-xl mb-1 text-[#2C3E50]">Vos informations</legend>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Prénom *">
                  <input className="input-square" value={form.requester_first_name} onChange={(e) => set("requester_first_name", e.target.value)} required data-testid="input-first-name" />
                </Field>
                <Field label="Nom *">
                  <input className="input-square" value={form.requester_last_name} onChange={(e) => set("requester_last_name", e.target.value)} required data-testid="input-last-name" />
                </Field>
              </div>
              <Field label="Fonction dans l'entreprise">
                <input className="input-square" value={form.requester_role} onChange={(e) => set("requester_role", e.target.value)} placeholder="ex: Gérant, Responsable communication…" data-testid="input-role" />
              </Field>
              <Field label="Email professionnel *">
                <input type="email" className="input-square" value={form.requester_email} onChange={(e) => set("requester_email", e.target.value)} required data-testid="input-email" />
              </Field>
            </fieldset>

            <fieldset className="space-y-5 border-t border-[#F0F2F5] pt-6">
              <legend className="font-heading font-extrabold text-xl mb-1 text-[#2C3E50]">Votre demande</legend>
              <Field label="Type de demande *">
                <select className="input-square" value={form.request_type} onChange={(e) => set("request_type", e.target.value)} data-testid="input-request-type">
                  {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Message détaillé *">
                <textarea rows={5} className="input-square !rounded-2xl" value={form.message} onChange={(e) => set("message", e.target.value)} required placeholder="Précisez les modifications souhaitées, la raison de la demande, etc." data-testid="input-message" />
              </Field>
              <Field label="Justificatif éventuel (texte libre)">
                <textarea rows={3} className="input-square !rounded-2xl" value={form.justification} onChange={(e) => set("justification", e.target.value)} placeholder="Ex. lien vers l'extrait KBIS, document justifiant votre qualité, référence extranet…" data-testid="input-justification" />
              </Field>
            </fieldset>

            {error && <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700 rounded-xl" data-testid="form-error">{error}</div>}

            <button type="submit" disabled={!canSubmit() || submitting} className="btn-primary btn-large w-full disabled:opacity-50 disabled:pointer-events-none" data-testid="submit-correction-btn">
              {submitting ? "Envoi…" : "Envoyer la demande"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>

            <p className="text-xs text-[#6B7280] text-center">
              Vos données ne serviront qu'à traiter votre demande. Voir notre{" "}
              <Link to="/confidentialite" className="underline hover:text-[#2C3E50]">politique de confidentialité</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">{label}</label>
    {children}
  </div>
);
